import React, { useState } from "react";
import axios from "axios";
import {
  Document, Packer, Paragraph, HeadingLevel, TextRun, AlignmentType,
  Table, TableRow, TableCell, WidthType, BorderStyle, ShadingType, LevelFormat,
  PageNumber, Footer, PageBreak,
} from "docx";
import { saveAs } from "file-saver";

// ─── Shared Layout Constants ────────────────────────────────────────────────
const TABLE_WIDTH = 9360;
const CELL_BORDER = { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" };
const CELL_BORDERS = { top: CELL_BORDER, bottom: CELL_BORDER, left: CELL_BORDER, right: CELL_BORDER };
const CELL_MARGINS = { top: 80, bottom: 80, left: 120, right: 120 };
const HEADER_SHADING = { fill: "003366", type: ShadingType.CLEAR };
const ACCENT_SHADING = { fill: "D5E8F0", type: ShadingType.CLEAR };
const GREEN_SHADING  = { fill: "C6EFCE", type: ShadingType.CLEAR };

// ─── Helpers ────────────────────────────────────────────────────────────────
const str    = (v) => (v == null ? "" : String(v));
const p      = (text, opts = {}) => new Paragraph({ children: [new TextRun({ text: str(text), ...opts })] });
const spacer = () => new Paragraph({ text: "" });
const boldPara = (label, value) =>
  new Paragraph({ children: [new TextRun({ text: str(label), bold: true }), new TextRun(str(value))] });
const heading1 = (text) => new Paragraph({ text: str(text), heading: HeadingLevel.HEADING_1 });
const heading2 = (text) => new Paragraph({ text: str(text), heading: HeadingLevel.HEADING_2 });
const heading3 = (text) =>
  new Paragraph({ children: [new TextRun({ text: str(text), bold: true, size: 24, color: "1F5C99" })] });

const headerRow = (labels, colWidths, shading = HEADER_SHADING) =>
  new TableRow({
    tableHeader: true,
    children: labels.map((label, i) =>
      new TableCell({
        borders: CELL_BORDERS, shading, margins: CELL_MARGINS,
        width: { size: colWidths[i], type: WidthType.DXA },
        children: [new Paragraph({ children: [new TextRun({ text: str(label), bold: true, color: shading === HEADER_SHADING ? "FFFFFF" : "000000" })] })],
      })
    ),
  });

const dataRow = (values, colWidths, shading = null) => {
  const safeValues = Array.isArray(values) ? values : [];
  return new TableRow({
    children: safeValues.map((val, i) => {
      const safeVal = val == null ? "" : val;
      return new TableCell({
        borders: CELL_BORDERS, margins: CELL_MARGINS,
        ...(shading ? { shading } : {}),
        width: { size: (colWidths[i] || 1000), type: WidthType.DXA },
        children: Array.isArray(safeVal) ? safeVal.map((line) => p(str(line))) : [p(str(safeVal))],
      });
    }),
  });
};

const buildTable = (headers, colWidths, rows, shading = HEADER_SHADING) =>
  new Table({
    width: { size: TABLE_WIDTH, type: WidthType.DXA },
    columnWidths: colWidths,
    rows: [headerRow(headers, colWidths, shading), ...rows],
  });

const bulletList = (items = []) =>
  items.map((item) =>
    new Paragraph({ numbering: { reference: "bullets", level: 0 }, children: [new TextRun(str(item))] })
  );

// ─── TOC Patcher ────────────────────────────────────────────────────────────
const patchDocxWithToc = async (blob) => {
  const JSZip = (await import("jszip")).default;
  const arrayBuffer = await blob.arrayBuffer();
  const zip = await JSZip.loadAsync(arrayBuffer);
  const docXml = await zip.file("word/document.xml").async("string");

  const tocXml = [
    '<w:p><w:pPr><w:pStyle w:val="Heading1"/></w:pPr>',
    '<w:r><w:rPr><w:b/><w:sz w:val="32"/></w:rPr>',
    '<w:t>Table of Contents</w:t></w:r></w:p>',
    '<w:p><w:r><w:fldChar w:fldCharType="begin" w:dirty="true"/></w:r>',
    '<w:r><w:instrText xml:space="preserve"> TOC \\o &quot;1-2&quot; \\h \\z \\u </w:instrText></w:r>',
    '<w:r><w:fldChar w:fldCharType="separate"/></w:r>',
    '<w:r><w:fldChar w:fldCharType="end"/></w:r></w:p>',
    '<w:p><w:r><w:t></w:t></w:r></w:p>',
    '<w:p><w:r><w:br w:type="page"/></w:r></w:p>',
  ].join("");

  const PAGE_BREAK_MARKER = '<w:pageBreakBefore/>';
  const markerIndex = docXml.indexOf(PAGE_BREAK_MARKER);

  let patchedDocXml;
  if (markerIndex !== -1) {
    const closingTag = '</w:p>';
    const closingIndex = docXml.indexOf(closingTag, markerIndex);
    if (closingIndex !== -1) {
      const insertAt = closingIndex + closingTag.length;
      patchedDocXml = docXml.slice(0, insertAt) + tocXml + docXml.slice(insertAt);
    } else {
      patchedDocXml = docXml.replace("<w:body>", "<w:body>" + tocXml);
    }
  } else {
    patchedDocXml = docXml.replace("<w:body>", "<w:body>" + tocXml);
  }

  zip.file("word/document.xml", patchedDocXml);
  const settingsXml = await zip.file("word/settings.xml").async("string");
  const patchedSettings = settingsXml.includes("w:updateFields")
    ? settingsXml
    : settingsXml.replace("</w:settings>", '<w:updateFields w:val="true"/></w:settings>');
  zip.file("word/settings.xml", patchedSettings);
  const patchedBuffer = await zip.generateAsync({ type: "arraybuffer" });
  return new Blob([patchedBuffer], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
};

// ─── LOCAL Proposal Builder ───────────────────────────────────────────────
const buildLocalProposalBlob = async (data) => {
  const d = Array.isArray(data) ? data[0] : data;
  const modelIncludes = d.financial_summary?.model_includes || {};
  const fin = d.financial_summary || {};

  // ── Title Section ──────────────────────────────────────────────────────────
  const titleSection = [
    new Paragraph({ text: "Technical and Financial Proposal", heading: HeadingLevel.TITLE, alignment: AlignmentType.CENTER }),
    spacer(),
    new Paragraph({ text: str(d.document_metadata?.project_title || "Technical and Financial Proposal"), alignment: AlignmentType.CENTER }),
    new Paragraph({ text: `Version ${str(d.document_metadata?.version)}`, alignment: AlignmentType.CENTER }),
    new Paragraph({ text: `Date: ${str(d.document_metadata?.date)}`, alignment: AlignmentType.CENTER }),
    spacer(), spacer(),
    boldPara("Client Name: ", d.document_metadata?.client_name),
    boldPara("Proposed By: ", d.document_metadata?.proposed_by),
    spacer(),
    p(d.document_metadata?.confidentiality_note),
    spacer(), spacer(),
    new Paragraph({ children: [new TextRun({ break: 1 })], pageBreakBefore: true }),
  ];

  // ── Project Summary ────────────────────────────────────────────────────────
  const projectSummarySection = [
    heading1("Project Summary"),
    boldPara("Solution", ""),
    p(d.project_summary?.solution),
    spacer(),
    boldPara("Impact", ""),
    p(d.project_summary?.impact),
    spacer(),
  ];

  // ── Project Scope ──────────────────────────────────────────────────────────
  const integrationRows = (d.project_scope?.integrations ?? []).map((i) =>
    dataRow([i.integration, i.partner, i.description], [2340, 2340, 4680])
  );
  const projectScopeSection = [
    heading1("Project Scope"),
    boldPara("Mobile Application", ""),
    p(d.project_scope?.mobile_application),
    spacer(),
    boldPara("Web Application", ""),
    p(d.project_scope?.web_application),
    spacer(),
    boldPara("Integrations", ""),
    buildTable(["Integration", "Partner", "Description"], [2340, 2340, 4680], integrationRows),
    spacer(),
  ];

  // ── Functional Requirements ────────────────────────────────────────────────
  const frSection = (title, features) => {
    if (!Array.isArray(features) || features.length === 0) return [];
    const colWidths = [2500, 4360, 2500];
    const rows = features.map((fr) =>
      dataRow([fr.feature, fr.description, Array.isArray(fr.flow) ? fr.flow : [str(fr.flow)]], colWidths)
    );
    return [heading2(title), buildTable(["Feature", "Description", "Flow (Step-wise)"], colWidths, rows), spacer()];
  };

  const functionalSection = [
    heading1("Functional Requirements"),
    ...frSection("User Features", d.functional_requirements?.user_features),
    ...frSection("Admin Features", d.functional_requirements?.admin_features),
  ];

  // ── Deliverables ───────────────────────────────────────────────────────────
  const deliverablesSection = [
    heading1("Deliverables"),
    ...(d.deliverables ?? []).map((item) =>
      new Paragraph({ numbering: { reference: "bullets", level: 0 }, children: [new TextRun(str(item))] })
    ),
    spacer(),
  ];

  // ── Project Plan ───────────────────────────────────────────────────────────
  const projectPlanRows = (d.project_plan?.milestones ?? []).map((m) =>
    dataRow(
      [
        m.milestone,
        Array.isArray(m.description_of_work) ? m.description_of_work : [],
        str(m.working_days),
        Array.isArray(m.responsibilities) ? m.responsibilities : [],
      ],
      [2500, 3000, 1500, 2360]
    )
  );
  const projectPlanSection = [
    heading1("Project Plan"),
    p(`Project Type: ${str(d.project_plan?.project_type)}`),
    spacer(),
    buildTable(
      ["Milestone", "Description of Work", "Working Days", "Responsibilities"],
      [2500, 3000, 1500, 2360],
      projectPlanRows
    ),
    spacer(),
    ...(d.project_plan_notes ? [heading2("Project Plan Notes"), p(d.project_plan_notes), spacer()] : []),
  ];

  // ── Financial Summary ──────────────────────────────────────────────────────
  const financialRows = (fin.categories ?? []).map((c) =>
    dataRow([c.category, c.amount_pkr?.toLocaleString() ?? "0", str(c.remarks)], [3500, 2500, 3360])
  );

  // Subtotal row (excl. tax) — blue accent
  const totalExclRow = new TableRow({
    children: [
      new TableCell({
        borders: CELL_BORDERS, margins: CELL_MARGINS, shading: ACCENT_SHADING,
        width: { size: 3500, type: WidthType.DXA },
        children: [new Paragraph({ children: [new TextRun({ text: "Total (Excl. Tax)", bold: true })] })],
      }),
      new TableCell({
        borders: CELL_BORDERS, margins: CELL_MARGINS, shading: ACCENT_SHADING,
        width: { size: 2500, type: WidthType.DXA },
        children: [new Paragraph({ children: [new TextRun({ text: fin.total_solution_price_excl_tax?.toLocaleString() ?? "0", bold: true })] })],
      }),
      new TableCell({
        borders: CELL_BORDERS, margins: CELL_MARGINS,
        width: { size: 3360, type: WidthType.DXA },
        children: [p("")],
      }),
    ],
  });

  // Grand total row (incl. tax) — green highlight
  const totalInclRow = new TableRow({
    children: [
      new TableCell({
        borders: CELL_BORDERS, margins: CELL_MARGINS, shading: GREEN_SHADING,
        width: { size: 3500, type: WidthType.DXA },
        children: [new Paragraph({ children: [new TextRun({ text: "Total (Incl. Tax)", bold: true })] })],
      }),
      new TableCell({
        borders: CELL_BORDERS, margins: CELL_MARGINS, shading: GREEN_SHADING,
        width: { size: 2500, type: WidthType.DXA },
        children: [new Paragraph({ children: [new TextRun({ text: fin.total_project_price_incl_tax?.toLocaleString() ?? "0", bold: true })] })],
      }),
      new TableCell({
        borders: CELL_BORDERS, margins: CELL_MARGINS,
        width: { size: 3360, type: WidthType.DXA },
        children: [p("")],
      }),
    ],
  });

  const financialSection = [
    heading1("Financial Summary"),

    // Model name
    ...(fin.model ? [heading2(`Model: ${str(fin.model)}`)] : []),
    spacer(),

    // Categories table — now includes both excl. and incl. tax total rows
    new Table({
      width: { size: TABLE_WIDTH, type: WidthType.DXA },
      columnWidths: [3500, 2500, 3360],
      rows: [
        headerRow(["Category", "Amount (PKR)", "Remarks"], [3500, 2500, 3360]),
        ...financialRows,
        totalExclRow,
        totalInclRow,
      ],
    }),
    spacer(),

    // Amount in words
    p(`In Words: ${str(fin.amount_in_words)}`),
    spacer(),

    // Numeric calculations summary
    boldPara("Solution Price (Excl. Tax): PKR ", fin.total_solution_price_excl_tax?.toLocaleString() ?? "0"),
    boldPara("Sales Tax (3% SST): PKR ", fin.sales_tax_3_percent?.toLocaleString() ?? "0"),
    boldPara("Total Project Price (Incl. Tax): PKR ", fin.total_project_price_incl_tax?.toLocaleString() ?? "0"),
    spacer(),

    // Monthly recurring — render only when non-zero
    ...((fin.monthly_recurring && fin.monthly_recurring !== 0)
      ? [
          boldPara("Monthly Recurring: PKR ", fin.monthly_recurring?.toLocaleString() ?? "0"),
          boldPara("Monthly Recurring (Incl. Tax): PKR ", fin.monthly_recurring_with_tax?.toLocaleString() ?? "0"),
          spacer(),
        ]
      : []),

    // Annual amounts — always render when present
    ...((fin.annual_amount || fin.annual_amount_with_tax)
      ? [
          boldPara("Annual Amount (Excl. Tax): PKR ", fin.annual_amount?.toLocaleString() ?? "0"),
          boldPara("Annual Amount (Incl. Tax): PKR ", fin.annual_amount_with_tax?.toLocaleString() ?? "0"),
          spacer(),
        ]
      : []),

    // Recurring model notes
    ...(fin.recurring_model_notes ? [p(fin.recurring_model_notes), spacer()] : []),

    // Model Includes
    heading2("Model Includes"),
    ...(modelIncludes.onsite_resources
      ? [boldPara("Onsite Resources: ", modelIncludes.onsite_resources), spacer()]
      : []),
    ...(modelIncludes.dedicated_resources?.length
      ? [
          new Paragraph({ children: [new TextRun({ text: "Dedicated Resources", bold: true })] }),
          ...bulletList(modelIncludes.dedicated_resources),
          spacer(),
        ]
      : []),
    ...(modelIncludes.duties?.length
      ? [
          new Paragraph({ children: [new TextRun({ text: "Duties Included", bold: true })] }),
          ...bulletList(modelIncludes.duties),
          spacer(),
        ]
      : []),
    ...(modelIncludes.out_of_scope
      ? [boldPara("Out of Scope: ", modelIncludes.out_of_scope), spacer()]
      : []),
    ...(modelIncludes.renewal_terms
      ? [boldPara("Renewal Terms: ", modelIncludes.renewal_terms), spacer()]
      : []),

    // Financial Summary Notes
    ...(d.financial_summary_notes
      ? [heading2("Financial Summary Notes"), p(d.financial_summary_notes), spacer()]
      : []),
  ];

  // ── Payment Terms ──────────────────────────────────────────────────────────
  const paymentRows = (d.payment_terms ?? []).map((pt) =>
    dataRow([str(pt.payment_percentage), str(pt.trigger), str(pt.details)], [1500, 3000, 4860])
  );
  const paymentSection = [
    heading1("Payment Terms"),
    buildTable(["%", "Milestone / Trigger", "Details"], [1500, 3000, 4860], paymentRows),
    spacer(),
    ...(d.payment_terms_notes
      ? [heading2("Payment Terms Notes"), p(d.payment_terms_notes), spacer()]
      : []),
  ];

  // ── Proposed Technology ────────────────────────────────────────────────────
  const techSection = [
    heading1("Proposed Technology"),
    boldPara("Frontend: ", d.proposed_technology?.frontend),
    boldPara("Backend: ", d.proposed_technology?.backend),
    boldPara("Database: ", d.proposed_technology?.database),
    boldPara("Hosting: ", d.proposed_technology?.hosting),
    boldPara("Security: ", d.proposed_technology?.security),
    spacer(),
  ];

  // ── Project Team ───────────────────────────────────────────────────────────
  const teamColWidths = [2500, 1500, 5360];
  const teamRows = (d.project_team ?? []).map((t) =>
    dataRow([t.role, t.type, t.duties], teamColWidths)
  );
  const teamSection = [
    heading1("Project Team"),
    buildTable(["Role", "Type", "Duties"], teamColWidths, teamRows),
    spacer(),
  ];

  // ── Out of Scope, Risks & Assumptions ─────────────────────────────────────
  const outOfScopeSection = d.out_of_scope_risks_assumptions
    ? [heading1("Out of Scope, Risks & Assumptions"), p(d.out_of_scope_risks_assumptions), spacer()]
    : [];

  // ── Publishing & Hosting Requirements ─────────────────────────────────────
  const hostingSection = d.publishing_hosting_requirements
    ? [heading1("Publishing & Hosting Requirements"), p(d.publishing_hosting_requirements), spacer()]
    : [];

  // ── Ownership, IP & Non-Disclosure ────────────────────────────────────────
  const ipSection = d.ownership_ip_non_disclosure
    ? [heading1("Ownership, IP & Non-Disclosure"), p(d.ownership_ip_non_disclosure), spacer()]
    : [];

  // ── Sign Offs ──────────────────────────────────────────────────────────────
  const signOffColWidths = [600, 1900, 2160, 1700, 1200, 1800];
  const signOffRows = (d.sign_offs ?? []).map((s) =>
    dataRow(
      [str(s.sno), str(s.name), str(s.company), str(s.designation), str(s.date), str(s.signature ?? "")],
      signOffColWidths
    )
  );
  const signOffSection = [
    heading1("Sign Offs"),
    buildTable(
      ["S.No", "Name", "Company", "Designation", "Date", "Signature"],
      signOffColWidths,
      signOffRows
    ),
    spacer(),
  ];

  // ── Assemble Document ──────────────────────────────────────────────────────
  const doc = new Document({
    numbering: {
      config: [{
        reference: "bullets",
        levels: [{
          level: 0,
          format: LevelFormat.BULLET,
          text: "•",
          alignment: AlignmentType.LEFT,
          style: { paragraph: { indent: { left: 720, hanging: 360 } } },
        }],
      }],
    },
    styles: {
      default: { document: { run: { font: "Arial", size: 24 } } },
      paragraphStyles: [
        {
          id: "Heading1", name: "Heading 1", basedOn: "Normal", next: "Normal", quickFormat: true,
          run: { size: 32, bold: true, font: "Arial", color: "2E5E8E" },
          paragraph: { spacing: { before: 300, after: 120 }, outlineLevel: 0 },
        },
        {
          id: "Heading2", name: "Heading 2", basedOn: "Normal", next: "Normal", quickFormat: true,
          run: { size: 28, bold: true, font: "Arial", color: "3A7DC9" },
          paragraph: { spacing: { before: 200, after: 80 }, outlineLevel: 1 },
        },
      ],
    },
    sections: [{
      properties: {
        page: {
          size: { width: 12240, height: 15840 },
          margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
        },
      },
      children: [
        ...titleSection,
        ...projectSummarySection,
        ...projectScopeSection,
        ...functionalSection,
        ...deliverablesSection,
        ...projectPlanSection,
        ...financialSection,
        ...paymentSection,
        ...techSection,
        ...teamSection,
        ...outOfScopeSection,
        ...hostingSection,
        ...ipSection,
        ...signOffSection,
      ],
    }],
  });

  return await Packer.toBlob(doc);
};

// ─── INTERNATIONAL Proposal Builder ──────────────────────────────────────────

const isTotalRow = (component) =>
  typeof component === "string" && component.toLowerCase().includes("total");

const parseStepsFromString = (str) => {
  const result = {};
  const lines = str.split("\n");
  let currentKey = null;
  let currentBullets = [];
  const flush = () => {
    if (currentKey && currentBullets.length) result[currentKey] = currentBullets;
  };
  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    const m = trimmed.match(/^Step\s+(\d+)\s*[–—-]\s*(.+)$/i);
    if (m) {
      flush();
      const label = m[2].trim().replace(/\s+/g, "_").toLowerCase();
      currentKey = `step_${m[1]}_${label}`;
      currentBullets = [];
    } else {
      const cleaned = trimmed.replace(/^[-•*]\s*/, "").trim();
      if (cleaned && currentKey) currentBullets.push(cleaned);
    }
  });
  flush();
  return result;
};

const extractIntegrationSteps = (rawScope) => {
  const intgRaw = rawScope.integration;
  if (intgRaw && typeof intgRaw === "object" && !Array.isArray(intgRaw) && Object.keys(intgRaw).length > 0)
    return intgRaw;
  if (typeof intgRaw === "string" && intgRaw.trim()) {
    const parsed = parseStepsFromString(intgRaw);
    if (Object.keys(parsed).length > 0) return parsed;
  }
  const allText = Object.values(rawScope)
    .filter((v) => typeof v === "string")
    .join("\n");
  if (/Step\s+\d+\s*[–—-]/i.test(allText)) {
    const parsed = parseStepsFromString(allText);
    if (Object.keys(parsed).length > 0) return parsed;
  }
  return {};
};

const toLines = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val.map(String).filter(Boolean);
  return String(val)
    .split(/\n+/)
    .map((s) => s.replace(/^[-•*]\s*/, "").trim())
    .filter(Boolean);
};

const normalizeInternational = (raw, inputOverrides = {}) => {
  const root = Array.isArray(raw) ? raw[0] : raw;
  const d = root.proposal || root;
  const rawScope = d.scope_of_work || {};
  const integrationNorm =
    (inputOverrides.scope_integration
      ? extractIntegrationSteps({ integration: inputOverrides.scope_integration })
      : null) ||
    extractIntegrationSteps(rawScope);

  const scopeNorm = {
    discovery_and_planning: toLines(rawScope.discovery_and_planning),
    design:                 toLines(rawScope.design || rawScope.ux_ui_design),
    development:            toLines(rawScope.development || rawScope.website_development),
    integration:            integrationNorm,
    testing:                toLines(rawScope.testing),
    deployment:             toLines(rawScope.deployment),
    content_development:    toLines(rawScope.content_development),
    interactive_features:   toLines(rawScope.interactive_features),
  };

  const rawFeatures = d.core_features || {};
  const functionalRaw = rawFeatures.functional_features || [];
  let categorizedFeatures = [];
  let flatFeatures = [];

  if (Array.isArray(functionalRaw)) {
    functionalRaw.forEach((item) => {
      if (item.category && Array.isArray(item.rows)) {
        categorizedFeatures.push({ category: item.category, rows: item.rows });
      } else if (item.feature) {
        flatFeatures.push({ feature: item.feature, description: item.description || "" });
      }
    });
  }

  const optionalFeatures =
    inputOverrides.optional_features ||
    rawFeatures.optional_features ||
    rawFeatures.optional_advanced_features ||
    [];
  const nonFunctionalFeatures =
    inputOverrides.non_functional_features ||
    rawFeatures.non_functional_features ||
    rawFeatures.non_functional ||
    [];

  const rawTech = inputOverrides.technology_stack || d.technology_stack || {};
  const techNorm = {
    frontend:          toLines(rawTech.frontend),
    backend:           toLines(rawTech.backend),
    database:          toLines(rawTech.database),
    integration_layer: toLines(rawTech.integration_layer),
    infrastructure:    toLines(rawTech.infrastructure),
    cloud_environment: toLines(rawTech.cloud_environment),
    security:          toLines(rawTech.security),
  };

  const rawResources = inputOverrides.resources || d.resources || [];
  const resources = rawResources.map((r) => ({
    role:           r.role || "",
    experience:     r.experience || r.relevant_experience || "",
    time_allocated: r.allocation || r.time_allocated || r.percent || "",
  }));

  const rawFin = inputOverrides.financials || d.financials || d.financial_proposal || {};
  const financialNorm = {
    implementation_costs: (rawFin.one_time_cost || rawFin.implementation_costs || []).map((c) => ({
      component:   c.component || "",
      description: c.description || "",
      amount:      c.amount || "",
      is_total:    c.is_total === true || isTotalRow(c.component),
    })),
    annual_support: (rawFin.annual_support_cost || rawFin.annual_support || []).map((c) => ({
      component:   c.component || "",
      description: c.description || "",
      amount:      c.amount || "",
      is_total:    c.is_total === true || isTotalRow(c.component),
    })),
    pricing_notes:       rawFin.pricing_notes || [],
    addons:              rawFin.addons || [],
    addon_section_title: rawFin.addon_section_title || "Optional Add-ons",
  };

  const rawTimeline = inputOverrides.timeline || d.timeline || d.project_timeline || {};
  const timelineNorm = {
    phases: Array.isArray(rawTimeline)
      ? rawTimeline.map((ph) => ({
          phase:               ph.phase || "",
          duration:            ph.duration || "",
          timeline_indicative: ph.timeline_indicative || ph.dates || "",
        }))
      : (rawTimeline.phases || []).map((ph) => ({
          phase:               ph.phase || "",
          duration:            ph.duration || "",
          timeline_indicative: ph.timeline_indicative || ph.dates || "",
        })),
    total: rawTimeline.total || "",
  };

  return {
    document_metadata: d.document_metadata || {},
    client_name:       d.client_name || "",
    project_title:     d.project_title || "",
    proposed_by:       d.proposed_by || "",
    executive_summary: Array.isArray(d.executive_summary)
      ? d.executive_summary.join("\n")
      : String(d.executive_summary || ""),
    problem_statement: Array.isArray(d.problem_statement)
      ? d.problem_statement.join("\n")
      : String(d.problem_statement || d.the_problem?.intro || ""),
    our_solution: Array.isArray(d.our_solution)
      ? d.our_solution.join("\n")
      : String(d.our_solution || ""),
    scope_of_work:    scopeNorm,
    core_features: {
      categorized:       categorizedFeatures,
      flat:              flatFeatures,
      optional_features: optionalFeatures,
      non_functional:    nonFunctionalFeatures,
    },
    project_timeline:  timelineNorm,
    financial_proposal: financialNorm,
    technology_stack:  techNorm,
    resources,
    why_choose_us:   d.why_choose_us  || {},
    company_profile: d.company_profile || {},
    portfolio:       d.portfolio      || {},
    prototype_note:  d.prototype_note || "",
  };
};

// ─── INTERNATIONAL Proposal Builder ──────────────────────────────────────────
const buildInternationalProposalBlob = async (data, inputOverrides = {}) => {
  const d = normalizeInternational(data, inputOverrides);

  const featureTable = (rows2col) => {
    const cw = [3120, 6240];
    return new Table({
      width: { size: TABLE_WIDTH, type: WidthType.DXA },
      columnWidths: cw,
      rows: [
        headerRow(["Features", "Description"], cw),
        ...rows2col.map(([f, desc]) => dataRow([f, desc], cw)),
      ],
    });
  };

  const triTable = (headers, colWidths, rows3) =>
    new Table({
      width: { size: TABLE_WIDTH, type: WidthType.DXA },
      columnWidths: colWidths,
      rows: [headerRow(headers, colWidths), ...rows3.map((r) => Array.isArray(r) ? dataRow(r, colWidths) : r)],
    });

  const parseParagraphAndBullets = (text) => {
    if (!text) return { paragraph: "", bullets: [] };
    const normalized = text
      .replace(/\r/g, "")
      .replace(/-\s\*\*/g, "• ")
      .replace(/\*\*/g, "")
      .replace(/^\s*-\s/gm, "• ")
      .replace(/^\s*\|\s/gm, "• ");

    const lines = normalized.split("\n").map((l) => l.trim()).filter(Boolean);
    const paragraphLines = [];
    const bullets = [];

    lines.forEach((line) => {
      if (line.startsWith("•")) {
        bullets.push(line.replace(/^•\s*/, ""));
      } else {
        paragraphLines.push(line);
      }
    });

    if (bullets.length === 0 && paragraphLines.length > 1) {
      return { paragraph: "", bullets: paragraphLines };
    }

    return { paragraph: paragraphLines.join(" "), bullets };
  };

  const buildTextSection = (title, content) => {
    const { paragraph, bullets } = parseParagraphAndBullets(content);
    return [
      heading1(title),
      paragraph ? p(paragraph) : null,
      ...(bullets.length ? bulletList(bullets) : []),
      spacer(),
    ].filter(Boolean);
  };

  const meta = d.document_metadata || {};

  const titleSection = [
    spacer(), spacer(),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new TextRun({ text: str(meta.client_name || d.client_name || "CLIENT NAME"), bold: true, size: 56, font: "Arial" })],
    }),
    spacer(),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new TextRun({ text: str(meta.project_title || d.project_title || "WEBSITE DESIGN & DEVELOPMENT"), bold: true, size: 48, font: "Arial" })],
    }),
    spacer(), spacer(),
    new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "Technical and Financial Proposal", size: 28, font: "Arial" })] }),
    spacer(),
    new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "____________________________________________________________", size: 24 })] }),
    spacer(), spacer(),
    new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "Prepared By:", bold: true, size: 28 })] }),
    new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: str(meta.proposed_by || d.proposed_by || ""), bold: true, size: 28 })] }),
    spacer(),
    ...(meta.primary_contacts?.length
      ? [
          new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: "Primary Contacts:", size: 24 })] }),
          ...meta.primary_contacts.map((c) =>
            new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: str(c), size: 24 })] })
          ),
        ]
      : []),
    spacer(),
    new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: `Date: ${str(meta.date || d.date || "")}`, size: 24 })] }),
    spacer(), spacer(), spacer(),
    new Paragraph({ children: [new TextRun({ break: 1 })], pageBreakBefore: true }),
  ];

  const execSection    = buildTextSection("1. Executive Summary", d.executive_summary);
  const problemSection = buildTextSection("2. The Problem",       d.problem_statement);
  const solutionSection= buildTextSection("3. Our Solution",      d.our_solution);

  const scope = d.scope_of_work || {};

  const buildScopeSubSection = (title, items) =>
    Array.isArray(items) && items.length
      ? [heading3(title), ...bulletList(items), spacer()]
      : [];

  const buildIntegrationSection = (integration) => {
    if (!integration || typeof integration !== "object" || Array.isArray(integration)) return [];
    const sections = [];

    const formatStepTitle = (key) => {
      const stepNum = key.match(/^step_(\d+)_/)?.[1];
      const label = key
        .replace(/^step_\d+_/, "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
      return stepNum ? `Step ${stepNum} – ${label}` : label;
    };

    const sortedKeys = Object.keys(integration).sort((a, b) => {
      const aNum = parseInt(a.match(/^step_(\d+)_/)?.[1] || 0);
      const bNum = parseInt(b.match(/^step_(\d+)_/)?.[1] || 0);
      return aNum - bNum;
    });

    sortedKeys.forEach((key) => {
      const value = integration[key];
      if (!value) return;
      const bullets = Array.isArray(value)
        ? value.map(String)
        : String(value).split("\n").map((line) => line.replace(/^[-•*]\s*/, "").trim()).filter(Boolean);
      sections.push(heading3(formatStepTitle(key)), ...bulletList(bullets), spacer());
    });
    return sections;
  };

  const scopeSection = [
    heading1("4. Scope of Work"),
    ...buildScopeSubSection("Discovery & Planning", scope.discovery_and_planning),
    ...buildScopeSubSection("Design",               scope.design),
    ...buildScopeSubSection("Development",          scope.development),
    ...(scope.integration && Object.keys(scope.integration).length
      ? [heading3("Integration"), ...buildIntegrationSection(scope.integration)]
      : []),
    ...buildScopeSubSection("Testing",              scope.testing),
    ...buildScopeSubSection("Deployment",           scope.deployment),
    ...buildScopeSubSection("Content Development",  scope.content_development),
    ...buildScopeSubSection("Interactive Features", scope.interactive_features),
  ];

  const features = d.core_features || {};
  const categorizedBlocks = (features.categorized || []).flatMap(({ category, rows }) => [
    heading3(category),
    featureTable((rows || []).map((r) => [r.feature || "", r.description || ""])),
    spacer(),
  ]);

  const flatBlock = (features.flat || []).length
    ? [heading2("Functional Features"), featureTable(features.flat.map((r) => [r.feature || "", r.description || ""])), spacer()]
    : [];

  const coreSection = [
    heading1("5. Core Features"),
    ...(categorizedBlocks.length ? [heading2("Functional Features"), ...categorizedBlocks] : []),
    ...flatBlock,
    ...(features.optional_features?.length
      ? [heading2("Optional / Advanced Features"), featureTable(features.optional_features.map((r) => [r.feature || "", r.description || ""])), spacer()]
      : []),
    ...(features.non_functional?.length
      ? [heading2("Non-Functional Features"), featureTable(features.non_functional.map((r) => [r.feature || "", r.description || ""])), spacer()]
      : []),
  ];

  const prototypeSection = [
    heading1("6. Prototype / UI Screens"),
    p("Our sample proposed UI draft for the homepage. We are open to update based on client preference if the project is awarded to us."),
    spacer(),
  ];

  const timelineObj = d.project_timeline || {};
  const timelineData = Array.isArray(timelineObj) ? timelineObj : (timelineObj.phases || []);
  const timelineTotal = timelineObj.total || "";
  const tlCw = [3800, 1560, 4000];
  const tlRows = timelineData.map((ph) => [str(ph.phase), str(ph.duration), str(ph.timeline_indicative || "")]);
  const timelineSection = [
    heading1("7. Project Timeline"),
    ...(tlRows.length ? [triTable(["Phase", "Duration", "Timeline (Indicative)"], tlCw, tlRows), spacer()] : []),
    ...(timelineTotal ? [boldPara("Total Estimated Timeline: ", timelineTotal), spacer()] : []),
  ];

  const financial = d.financial_proposal || {};

  const implCw = [3000, 4860, 1500];
  const implRows = (financial.implementation_costs || []).map((c) => {
    const isTotal = c.is_total;
    return new TableRow({
      children: [
        new TableCell({ borders: CELL_BORDERS, margins: CELL_MARGINS, ...(isTotal ? { shading: GREEN_SHADING } : {}), width: { size: implCw[0], type: WidthType.DXA }, children: [new Paragraph({ children: [new TextRun({ text: str(c.component), bold: isTotal })] })] }),
        new TableCell({ borders: CELL_BORDERS, margins: CELL_MARGINS, ...(isTotal ? { shading: GREEN_SHADING } : {}), width: { size: implCw[1], type: WidthType.DXA }, children: [new Paragraph({ children: [new TextRun({ text: str(c.description), bold: isTotal })] })] }),
        new TableCell({ borders: CELL_BORDERS, margins: CELL_MARGINS, ...(isTotal ? { shading: GREEN_SHADING } : {}), width: { size: implCw[2], type: WidthType.DXA }, children: [new Paragraph({ children: [new TextRun({ text: str(c.amount), bold: isTotal })] })] }),
      ],
    });
  });

  const annualCw = [2800, 4560, 2000];
  const annualRows = (financial.annual_support || []).map((c) => {
    const isTotal = c.is_total;
    return new TableRow({
      children: [
        new TableCell({ borders: CELL_BORDERS, margins: CELL_MARGINS, ...(isTotal ? { shading: GREEN_SHADING } : {}), width: { size: annualCw[0], type: WidthType.DXA }, children: [new Paragraph({ children: [new TextRun({ text: str(c.component), bold: isTotal })] })] }),
        new TableCell({ borders: CELL_BORDERS, margins: CELL_MARGINS, ...(isTotal ? { shading: GREEN_SHADING } : {}), width: { size: annualCw[1], type: WidthType.DXA }, children: [new Paragraph({ children: [new TextRun({ text: str(c.description), bold: isTotal })] })] }),
        new TableCell({ borders: CELL_BORDERS, margins: CELL_MARGINS, ...(isTotal ? { shading: GREEN_SHADING } : {}), width: { size: annualCw[2], type: WidthType.DXA }, children: [new Paragraph({ children: [new TextRun({ text: str(c.amount), bold: isTotal })] })] }),
      ],
    });
  });

  const addonCw = [6360, 3000];
  const addonRows = (financial.addons || []).map((a) => dataRow([a.module || a.name || "", str(a.cost)], addonCw));

  const financialSection = [
    heading1("8. Financial Proposal"),
    heading2("One-Time Implementation Cost"),
    ...(implRows.length
      ? [new Table({ width: { size: TABLE_WIDTH, type: WidthType.DXA }, columnWidths: implCw, rows: [headerRow(["Cost Component", "Description", "Amount"], implCw), ...implRows] }), spacer()]
      : []),
    ...(annualRows.length
      ? [heading2("Annual Support"), new Table({ width: { size: TABLE_WIDTH, type: WidthType.DXA }, columnWidths: annualCw, rows: [headerRow(["Annual Component", "Description", "Amount"], annualCw), ...annualRows] }), spacer()]
      : []),
    ...(financial.pricing_notes?.length ? [heading2("Pricing Notes"), ...bulletList(financial.pricing_notes), spacer()] : []),
    ...(addonRows.length
      ? [heading2(financial.addon_section_title || "Optional Add-ons"), new Table({ width: { size: TABLE_WIDTH, type: WidthType.DXA }, columnWidths: addonCw, rows: [headerRow(["Module", "Cost"], addonCw), ...addonRows] }), spacer()]
      : []),
  ];

  const tech = d.technology_stack || {};
  const buildTechSubSection = (label, items) =>
    items?.length ? [new Paragraph({ children: [new TextRun({ text: label + ":", bold: true })] }), ...bulletList(items), spacer()] : [];

  const techSection = [
    heading1("9. Technology Stack & Architecture"),
    ...buildTechSubSection("Frontend",           tech.frontend),
    ...buildTechSubSection("Backend",            tech.backend),
    ...buildTechSubSection("Database",           tech.database),
    ...buildTechSubSection("Integration Layer",  tech.integration_layer),
    ...buildTechSubSection("Infrastructure",     tech.infrastructure),
    ...buildTechSubSection("Cloud Environment",  tech.cloud_environment),
    ...buildTechSubSection("Security",           tech.security),
  ];

  const resCw = [2000, 5860, 1500];
  const resRows = d.resources && d.resources.length > 0
    ? d.resources.map((r) => dataRow([r.role, r.experience, r.time_allocated], resCw))
    : [
        dataRow(["Project Manager",   "8+ years managing website development & integration projects", "40%"], resCw),
        dataRow(["Business Analyst",  "5+ years in requirement gathering and stakeholder facilitation", "15%"], resCw),
        dataRow(["Web Developer",     "6+ years experience in CMS, responsive design, dynamic features", "35%"], resCw),
        dataRow(["Backend Developer", "5+ years in API development, database integration", "30%"], resCw),
        dataRow(["DevOps Engineer",   "5+ years in cloud hosting, CI/CD, and system monitoring", "10%"], resCw),
        dataRow(["Security Officer",  "5+ years in web application security and compliance", "5%"],  resCw),
        dataRow(["QA / Tester",       "4+ years in functional, accessibility, and performance testing", "20%"], resCw),
      ];

  const resourcesSection = [
    heading1("10. Resources"),
    new Table({ width: { size: TABLE_WIDTH, type: WidthType.DXA }, columnWidths: resCw, rows: [headerRow(["Role", "Relevant Experience", "% of Time Allocated"], resCw), ...resRows] }),
    spacer(),
  ];

  const whyChooseSection = [
    heading1("11. Why Choose UHF Technologies Solutions"),
    ...bulletList([
      "Proven enterprise-grade digital platform experience",
      "Government-grade compliance expertise",
      "Strong CRM & analytics background",
      "Predictable cost structure",
      "Long-term partnership approach",
    ]),
    spacer(),
    new Paragraph({ children: [new TextRun({ text: "Primary Contacts:", bold: true })] }),
    ...bulletList([
      "Pre-Sales Consultant: usman.bukhari@uhfsolutions.com",
      "Head of Global Partnerships: shahbaz@uhfsolutions.com",
      "Global Director – Business Development: haris@uhfsolutions.com",
    ]),
    spacer(),
  ];

  const companyProfileSection = [
    heading1("12. Company Profile"),
    p("UHF Technologies Solutions specializes in delivering customized software development and tech consultancy services to help businesses thrive in today's competitive landscape."),
    spacer(),
    p("Our expertise spans in enterprise applications, business process automation, customer-facing applications, product design & development, e-commerce solutions, and cloud services. We cater to diverse domains like finance, supply chain, and fitness etc, seamlessly integrating with systems like SAP, Oracle, Dynamics, and core banking applications delivering innovation and technical excellence."),
    spacer(),
    p("Over the past decade, we have grown from a small team of eight to a dynamic workforce of 100+ professionals, serving clients globally."),
    spacer(),
    new Paragraph({ children: [new TextRun({ text: "Key Highlights:", bold: true })] }),
    ...bulletList(["100+ Employees", "200+ Successful Projects", "80+ Global Clients", "10+ Years of Experience"]),
    spacer(),
  ];

  const portfolioSection = [
    heading1("13. Some Reference Portfolio"),
    p("Enterprise level digital platforms for:"),
    ...bulletList([
      "https://www.hotworx.net/ (Over 300K Users)",
      "https://liferisesolutions.com/",
      "https://falloninsuranceagency.com/",
      "https://www.anjumanwazifa.org/ (Over 100k Users)",
      "https://ir.avnet.com/",
      "https://www.cummins.com/",
      "https://www.pbs.gov.pk/",
      "https://edf.gov.pk/",
      "https://www.spra.com/",
      "https://landopia.com/",
      "https://www.hesstruckcollectors.com/",
      "https://sevenstarinvestments.com/",
      "https://magalumllc.com/",
      "https://weblogik.com/",
      "https://websitedojo.com/",
    ]),
    spacer(),
  ];

  const doc = new Document({
    numbering: {
      config: [{
        reference: "bullets",
        levels: [{
          level: 0, format: LevelFormat.BULLET, text: "•", alignment: AlignmentType.LEFT,
          style: { paragraph: { indent: { left: 720, hanging: 360 } } },
        }],
      }],
    },
    styles: {
      default: { document: { run: { font: "Arial", size: 22 } } },
      paragraphStyles: [
        {
          id: "Heading1", name: "Heading 1", basedOn: "Normal", next: "Normal", quickFormat: true,
          run: { size: 32, bold: true, font: "Arial", color: "003366" },
          paragraph: { spacing: { before: 320, after: 160 }, outlineLevel: 0 },
        },
        {
          id: "Heading2", name: "Heading 2", basedOn: "Normal", next: "Normal", quickFormat: true,
          run: { size: 28, bold: true, font: "Arial", color: "1F5C99" },
          paragraph: { spacing: { before: 200, after: 100 }, outlineLevel: 1 },
        },
      ],
    },
    sections: [{
      properties: {
        page: {
          size: { width: 12240, height: 15840 },
          margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
        },
      },
      footers: {
        default: new Footer({
          children: [
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [
                new TextRun({ text: str(meta.proposed_by || ""), size: 18, color: "888888" }),
                new TextRun({ text: "  •  Page ", size: 18, color: "888888" }),
                new TextRun({ children: [PageNumber.CURRENT], size: 18, color: "888888" }),
              ],
            }),
          ],
        }),
      },
      children: [
        ...titleSection,
        ...execSection,
        ...problemSection,
        ...solutionSection,
        ...scopeSection,
        ...coreSection,
        ...prototypeSection,
        ...timelineSection,
        ...financialSection,
        ...techSection,
        ...resourcesSection,
        ...whyChooseSection,
        ...companyProfileSection,
        ...portfolioSection,
      ],
    }],
  });

  return await Packer.toBlob(doc);
};

const buildProposalBlob = async (data, proposalType, inputOverrides = {}) => {
  if (proposalType === "International") {
    return buildInternationalProposalBlob(data, inputOverrides);
  }
  return buildLocalProposalBlob(data);
};

// ─── CSS ────────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700&family=DM+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .portal-bg {
    min-height: 100vh;
    background: linear-gradient(135deg, #eef2ff 0%, #f0fdf4 50%, #faf5ff 100%);
    display: flex; flex-direction: column;
    position: relative; overflow: hidden;
    font-family: 'Sora', sans-serif;
  }
  .portal-bg::before {
    content: ''; position: fixed; top: -120px; left: -120px;
    width: 500px; height: 500px;
    background: radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%);
    border-radius: 50%; pointer-events: none; z-index: 0;
  }
  .portal-bg::after {
    content: ''; position: fixed; bottom: -100px; right: -100px;
    width: 450px; height: 450px;
    background: radial-gradient(circle, rgba(16,185,129,0.10) 0%, transparent 70%);
    border-radius: 50%; pointer-events: none; z-index: 0;
  }
  .card {
    background: transparent; width: 100%; flex: 1; overflow: hidden;
    position: relative; z-index: 1; display: flex; flex-direction: column;
    animation: slideUp 0.5s cubic-bezier(0.16,1,0.3,1) both;
  }
  @keyframes slideUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
  .card-header {
    background: linear-gradient(180deg, #004687 0%, #003366 100%);
    padding: 40px 60px 32px; position: relative; overflow: hidden;
  }
  .card-header::before {
    content:''; position:absolute; top:-40px; right:-40px;
    width:180px; height:180px; background:rgba(255,255,255,0.07); border-radius:50%;
  }
  .card-header::after {
    content:''; position:absolute; bottom:-60px; left:20px;
    width:140px; height:140px; background:rgba(255,255,255,0.05); border-radius:50%;
  }
  .card-title { font-size:26px; font-weight:700; color:#fff; line-height:1.2; margin-bottom:6px; position:relative; z-index:1; }
  .card-subtitle { font-size:13px; color:rgba(255,255,255,0.65); font-weight:300; position:relative; z-index:1; }
  .step-bar {
    display:flex; align-items:center; padding:0 60px;
    background:#f8f8fe; border-bottom:1px solid #e5e7eb;
  }
  .step-item { display:flex; align-items:center; gap:8px; padding:18px 0; flex:0 0 auto; }
  .step-dot {
    width:28px; height:28px; border-radius:50%; border:2px solid #d1d5db; background:#fff;
    display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700;
    color:#6b7280; transition:all 0.3s ease; flex-shrink:0;
  }
  .step-item.active .step-dot { background:linear-gradient(180deg, #004687 0%, #003366 100%); border-color:#004687; color:#fff; box-shadow:0 0 0 3px rgba(0,70,135,0.15); }
  .step-item.done .step-dot { background:#10b981; border-color:#10b981; color:#fff; }
  .step-label { font-size:12px; font-weight:600; color:#9ca3af; white-space:nowrap; }
  .step-item.active .step-label { color:#004687; }
  .step-item.done .step-label { color:#10b981; }
  .step-divider { width:48px; height:2px; background:#e5e7eb; margin:0 12px; flex-shrink:0; }
  .card-body { padding:48px 60px; animation:fadeIn 0.3s ease both; max-width:860px; }
  @keyframes fadeIn { from { opacity:0; transform:translateX(10px); } to { opacity:1; transform:translateX(0); } }
  .section-label { font-size:11px; font-weight:600; letter-spacing:0.1em; color:#9ca3af; text-transform:uppercase; margin-bottom:14px; font-family:'DM Mono',monospace; }
  .section-title { font-size:18px; font-weight:700; color:#111827; margin-bottom:8px; }
  .section-desc { font-size:13px; color:#6b7280; margin-bottom:28px; line-height:1.6; }
  .type-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(240px, 340px)); gap:18px; margin-bottom:40px; }
  .type-card {
    border:2px solid #e5e7eb; border-radius:16px; padding:28px 24px; cursor:pointer;
    transition:all 0.2s ease; background:#fff; text-align:center; position:relative; overflow:hidden;
  }
  .type-card:hover { border-color:#a5b4fc; box-shadow:0 4px 16px rgba(99,102,241,0.12); transform:translateY(-2px); }
  .type-card.selected { border-color:#004687; background:linear-gradient(135deg,#f0f7ff,#e8f0fe); box-shadow:0 4px 20px rgba(0,70,135,0.15); }
  .check-ring {
    position:absolute; top:12px; right:12px; width:20px; height:20px;
    border-radius:50%; border:2px solid #d1d5db; transition:all 0.2s ease;
    display:flex; align-items:center; justify-content:center;
  }
  .type-card.selected .check-ring { background:#004687; border-color:#004687; }
  .check-ring::after { content:'✓'; color:#fff; font-size:11px; font-weight:700; opacity:0; transition:opacity 0.2s; }
  .type-card.selected .check-ring::after { opacity:1; }
  .type-icon { font-size:32px; margin-bottom:12px; display:block; }
  .type-name { font-size:15px; font-weight:700; color:#111827; margin-bottom:4px; }
  .type-desc { font-size:11px; color:#9ca3af; line-height:1.4; }
  .type-badge {
    display:inline-block; margin-top:8px; font-size:10px; font-weight:700; letter-spacing:0.05em;
    padding:3px 10px; border-radius:20px; text-transform:uppercase;
  }
  .type-badge.local { background:#fef9c3; color:#854d0e; }
  .type-badge.intl  { background:#dbeafe; color:#1e40af; }
  .method-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(220px, 320px)); gap:14px; margin-bottom:24px; }
  .method-card {
    border:2px solid #e5e7eb; border-radius:14px; padding:18px 16px; cursor:pointer;
    transition:all 0.2s ease; background:#fff; display:flex; align-items:center; gap:14px;
  }
  .method-card:hover { border-color:#a5b4fc; transform:translateY(-1px); box-shadow:0 4px 12px rgba(99,102,241,0.10); }
  .method-card.selected { border-color:#004687; background:linear-gradient(135deg,#f0f7ff,#e8f0fe); }
  .method-icon-wrap {
    width:42px; height:42px; border-radius:10px; background:#f3f4f6;
    display:flex; align-items:center; justify-content:center; font-size:20px; flex-shrink:0; transition:background 0.2s;
  }
  .method-card.selected .method-icon-wrap { background:rgba(0,70,135,0.10); }
  .method-name { font-size:14px; font-weight:700; color:#111827; margin-bottom:2px; }
  .method-hint { font-size:11px; color:#9ca3af; }
  .input-area {
    width:100%; max-width:760px; min-height:180px; padding:18px; border:2px solid #e5e7eb; border-radius:12px;
    font-family:'DM Mono',monospace; font-size:13px; color:#374151; resize:vertical;
    transition:border-color 0.2s,box-shadow 0.2s; background:#fafafa; outline:none; margin-bottom:28px; line-height:1.6; display:block;
  }
  .input-area:focus { border-color:#004687; box-shadow:0 0 0 3px rgba(0,70,135,0.10); background:#fff; }
  .input-area::placeholder { color:#c4c9d4; }
  .drop-zone {
    border:2px dashed #d1d5db; border-radius:14px; padding:56px 32px; text-align:center;
    cursor:pointer; transition:all 0.2s ease; background:#fafafa; margin-bottom:28px; position:relative;
    max-width:760px;
  }
  .drop-zone:hover, .drop-zone.drag-over { border-color:#004687; background:rgba(0,70,135,0.03); }
  .drop-zone input[type="file"] { position:absolute; inset:0; opacity:0; cursor:pointer; width:100%; height:100%; }
  .drop-icon { font-size:36px; margin-bottom:10px; display:block; }
  .drop-text { font-size:14px; font-weight:600; color:#374151; margin-bottom:4px; }
  .drop-hint { font-size:12px; color:#9ca3af; }
  .file-chip {
    display:inline-flex; align-items:center; gap:8px; background:#f0fdf4;
    border:1px solid #bbf7d0; color:#15803d; font-size:12px; font-weight:600;
    padding:6px 14px; border-radius:20px; margin-top:12px;
  }
  .btn-row { display:flex; gap:12px; max-width:480px; }
  .btn {
    flex:1; padding:14px 20px; border:none; border-radius:12px; font-family:'Sora',sans-serif;
    font-size:14px; font-weight:700; cursor:pointer; transition:all 0.2s ease;
    display:flex; align-items:center; justify-content:center; gap:8px;
  }
  .btn-back { background:#f3f4f6; color:#6b7280; flex:0 0 auto; padding:14px 20px; }
  .btn-back:hover { background:#e5e7eb; color:#374151; }
  .btn-primary { background:linear-gradient(180deg, #004687 0%, #003366 100%); color:#fff; box-shadow:0 4px 16px rgba(0,70,135,0.3); }
  .btn-primary:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 6px 20px rgba(0,70,135,0.4); }
  .btn-primary:disabled { opacity:0.6; cursor:not-allowed; transform:none; }
  .btn-download { background:linear-gradient(135deg,#059669,#10b981); color:#fff; box-shadow:0 4px 16px rgba(16,185,129,0.3); animation:popIn 0.4s cubic-bezier(0.16,1,0.3,1) both; }
  .btn-download:hover { transform:translateY(-1px); box-shadow:0 6px 20px rgba(16,185,129,0.4); }
  @keyframes popIn { from { opacity:0; transform:scale(0.95) translateY(6px); } to { opacity:1; transform:scale(1) translateY(0); } }
  .success-banner {
    background:linear-gradient(135deg,#f0fdf4,#ecfdf5); border:1px solid #bbf7d0;
    border-radius:14px; padding:18px 20px; margin-bottom:20px; display:flex; align-items:center; gap:16px;
    animation:popIn 0.4s cubic-bezier(0.16,1,0.3,1) both; max-width:760px;
  }
  .success-icon { width:44px; height:44px; border-radius:50%; background:#dcfce7; display:flex; align-items:center; justify-content:center; font-size:22px; flex-shrink:0; }
  .success-text { font-size:14px; font-weight:600; color:#15803d; margin-bottom:2px; }
  .success-sub { font-size:12px; color:#6b7280; }
  .error-banner {
    background:linear-gradient(135deg,#fff5f5,#fef2f2); border:1px solid #fecaca;
    border-radius:14px; padding:18px 20px; margin-bottom:24px;
    display:flex; align-items:flex-start; gap:16px;
    animation:popIn 0.4s cubic-bezier(0.16,1,0.3,1) both; max-width:760px;
  }
  .error-icon-wrap { width:44px; height:44px; border-radius:50%; background:#fee2e2; display:flex; align-items:center; justify-content:center; font-size:20px; flex-shrink:0; }
  .error-title { font-size:14px; font-weight:700; color:#b91c1c; margin-bottom:6px; }
  .error-msg {
    font-size:12px; color:#6b7280; line-height:1.6;
    font-family:'DM Mono',monospace; word-break:break-word;
    background:#fff; border:1px solid #fecaca; border-radius:8px;
    padding:10px 14px; margin-top:4px; max-height:120px; overflow-y:auto;
  }
  .error-retry {
    margin-top:10px; font-size:12px; color:#b91c1c; font-weight:600; cursor:pointer;
    background:none; border:none; padding:0; text-decoration:underline; font-family:'Sora',sans-serif;
  }
  .error-retry:hover { color:#991b1b; }
  @keyframes spin { to { transform:rotate(360deg); } }
  .spinner { width:18px; height:18px; border:2.5px solid rgba(255,255,255,0.3); border-top-color:#fff; border-radius:50%; animation:spin 0.7s linear infinite; display:inline-block; }
`;

// ─── Error Helper ────────────────────────────────────────────────────────────
const extractErrorMessage = (err) => {
  if (err?.response) {
    const status = err.response.status;
    const data = err.response.data;
    let body = "";
    if (typeof data === "string") body = data;
    else if (data?.message) body = data.message;
    else if (data?.error) body = data.error;
    else body = JSON.stringify(data);
    return `Workflow responded with ${status}: ${body}`;
  }
  if (err?.request) return "No response from the workflow server. Check your connection or the webhook URL.";
  return err?.message || "An unknown error occurred.";
};

// ─── Main Component ──────────────────────────────────────────────────────────
export default function ProposalPage() {
  const [step, setStep] = useState(1);
  const [proposalType, setProposalType] = useState("");
  const [inputMethod, setInputMethod] = useState("");
  const [textInput, setTextInput] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadReady, setDownloadReady] = useState(null);
  const [workflowError, setWorkflowError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const reset = () => { setDownloadReady(null); setWorkflowError(null); };
  const handleFileChange = (f) => { if (f) { setFile(f); reset(); } };

  const extractInputOverrides = async () => {
    let rawText = textInput;

    if (file && !rawText) {
      const ext = file.name.split(".").pop().toLowerCase();
      try {
        if (ext === "docx" || ext === "doc") {
          const JSZip = (await import("jszip")).default;
          const ab = await file.arrayBuffer();
          const zip = await JSZip.loadAsync(ab);
          const docXml = await zip.file("word/document.xml")?.async("string");
          rawText = docXml || "";
        } else {
          rawText = await new Promise((res) => {
            const reader = new FileReader();
            reader.onload = (e) => res(e.target.result || "");
            reader.onerror = () => res("");
            reader.readAsText(file);
          });
        }
      } catch { rawText = ""; }
    }

    if (!rawText) return {};

    try {
      const parsed = JSON.parse(rawText);
      const root = Array.isArray(parsed) ? parsed[0] : parsed;
      const d = root?.proposal || root || {};
      const rawFeatures = d.core_features || {};
      const overrides = {};

      const optFeats = rawFeatures.optional_features || rawFeatures.optional_advanced_features;
      if (Array.isArray(optFeats) && optFeats.length > 0)
        overrides.optional_features = optFeats;

      const nonFuncFeats = rawFeatures.non_functional_features || rawFeatures.non_functional;
      if (Array.isArray(nonFuncFeats) && nonFuncFeats.length > 0)
        overrides.non_functional_features = nonFuncFeats;

      const scopeIntg = d.scope_of_work?.integration;
      if (scopeIntg) overrides.scope_integration = scopeIntg;

      const rawTech = d.technology_stack || {};
      if (Object.keys(rawTech).length > 0) overrides.technology_stack = rawTech;

      const rawRes = d.resources;
      if (Array.isArray(rawRes) && rawRes.length > 0) overrides.resources = rawRes;

      const rawFin = d.financials || d.financial_proposal;
      if (rawFin) overrides.financials = rawFin;

      const rawTimeline = d.timeline || d.project_timeline;
      if (rawTimeline) overrides.timeline = rawTimeline;

      return overrides;
    } catch {
      return {};
    }
  };

  const handleSubmit = async () => {
    if (!textInput && !file) { alert("Please provide text or upload a file"); return; }
    reset();
    setLoading(true);
    try {
      const inputOverrides = await extractInputOverrides();

      const formData = new FormData();
      formData.append("proposalType", proposalType);
      formData.append("text", textInput);
      if (file) formData.append("file", file);

const response = await axios.post(
        "https://aiuhf.app.n8n.cloud/webhook/84403134-87e8-4cd0-888f-09374d6c1792",
       formData,
  { timeout: 600000 }
);


      const rawBlob = await buildProposalBlob(response.data, proposalType, inputOverrides);
      const blob = await patchDocxWithToc(rawBlob);
      setDownloadReady({ blob, filename: `${proposalType}_Proposal.docx` });
      setTextInput("");
      setFile(null);
    } catch (err) {
      console.error(err);
      setWorkflowError(extractErrorMessage(err));
    }
    setLoading(false);
  };

  const handleDownload = () => {
    if (!downloadReady) return;
    saveAs(downloadReady.blob, downloadReady.filename);
  };

  const canProceedStep1 = !!proposalType;
  const canSubmit = inputMethod && (inputMethod === "text" ? !!textInput.trim() : !!file);
  const stepStatus = (n) => n < step ? "done" : n === step ? "active" : "";

  return (
    <>
      <style>{css}</style>
      <div className="portal-bg">
        <div className="card">

          <div className="card-header">
            <div className="card-title">Generate Your Proposal</div>
            <div className="card-subtitle">Create a professional technical & financial proposal document</div>
          </div>

          <div className="step-bar">
            <div className={`step-item ${stepStatus(1)}`}>
              <div className="step-dot">{stepStatus(1) === "done" ? "✓" : "1"}</div>
              <span className="step-label">Proposal Type</span>
            </div>
            <div className="step-divider" />
            <div className={`step-item ${stepStatus(2)}`}>
              <div className="step-dot">{stepStatus(2) === "done" ? "✓" : "2"}</div>
              <span className="step-label">Requirements</span>
            </div>
          </div>

          {step === 1 && (
            <div className="card-body" key="step1">
              <div className="section-label">Step 01 of 02</div>
              <div className="section-title">Select Proposal Type</div>
              <div className="section-desc">
                Choose the scope of your proposal - local projects or international client engagements.
              </div>

              <div className="type-grid">
                {[
                  { value: "Local",         icon: "🏢", name: "Local",         badge: "local" },
                  { value: "International", icon: "🌐", name: "International", badge: "intl"  },
                ].map((opt) => (
                  <div
                    key={opt.value}
                    className={`type-card ${proposalType === opt.value ? "selected" : ""}`}
                    onClick={() => { setProposalType(opt.value); reset(); }}
                  >
                    <div className="check-ring" />
                    <span className="type-icon">{opt.icon}</span>
                    <div className="type-name">{opt.name}</div>
                    <div className="type-desc">{opt.desc}</div>
                    <span className={`type-badge ${opt.badge}`}>{opt.badgeText}</span>
                  </div>
                ))}
              </div>

              <div className="btn-row">
                <button className="btn btn-primary" disabled={!canProceedStep1} onClick={() => setStep(2)}>
                  Continue →
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="card-body" key="step2">
              <div className="section-label">Step 02 of 02</div>
              <div className="section-title">Add Requirements</div>
              <div className="section-desc">
                Choose how you'd like to provide project requirements.
              </div>

              <div className="method-grid">
                {[
                  { value: "text", icon: "📝", name: "Plain Text", hint: "Paste or type requirements" },
                  { value: "file", icon: "📎", name: "Upload File", hint: "PDF or TXT" },
                ].map((m) => (
                  <div
                    key={m.value}
                    className={`method-card ${inputMethod === m.value ? "selected" : ""}`}
                    onClick={() => { setInputMethod(m.value); reset(); }}
                  >
                    <div className="method-icon-wrap">{m.icon}</div>
                    <div>
                      <div className="method-name">{m.name}</div>
                      <div className="method-hint">{m.hint}</div>
                    </div>
                  </div>
                ))}
              </div>

              {inputMethod === "text" && (
                <textarea
                  className="input-area"
                  placeholder="Describe the project requirements, features, integrations, and any specific needs…"
                  value={textInput}
                  onChange={(e) => { setTextInput(e.target.value); reset(); }}
                />
              )}

              {inputMethod === "file" && (
                <>
                  <div
                    className={`drop-zone ${dragOver ? "drag-over" : ""}`}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFileChange(e.dataTransfer.files[0]); }}
                  >
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.txt"
                      onChange={(e) => handleFileChange(e.target.files[0])}
                    />
                    <span className="drop-icon">☁️</span>
                    <div className="drop-text">{file ? "File attached" : "Drop your file here"}</div>
                    <div className="drop-hint">or click to browse — PDF, DOCX or TXT</div>
                  </div>
                  {file && (
                    <div className="file-chip" style={{ marginTop: 10 }}>
                      ✅ {file.name}
                      <button
                        onClick={() => setFile(null)}
                        style={{ marginLeft: 8, background: "none", border: "none", cursor: "pointer", color: "#15803d", fontSize: 15, lineHeight: 1, padding: "0 2px", fontWeight: "bold" }}
                        title="Remove file"
                      >✕</button>
                    </div>
                  )}
                </>
              )}

              {workflowError && (
                <div className="error-banner">
                  <div className="error-icon-wrap">⚠️</div>
                  <div style={{ flex: 1 }}>
                    <div className="error-title">Workflow Error</div>
                    <div className="error-msg">{workflowError}</div>
                    <button className="error-retry" onClick={reset}>Dismiss and try again</button>
                  </div>
                </div>
              )}

              {downloadReady && (
                <div className="success-banner">
                  <div className="success-icon">✅</div>
                  <div>
                    <div className="success-text">Proposal ready to download!</div>
                    <div className="success-sub">{downloadReady.filename}</div>
                  </div>
                </div>
              )}

              <div className="btn-row">
                <button className="btn btn-back" onClick={() => { setStep(1); reset(); }}>← Back</button>
                {!downloadReady ? (
                  <button className="btn btn-primary" disabled={!canSubmit || loading} onClick={handleSubmit}>
                    {loading ? <><span className="spinner" /> Generating…</> : "✦ Generate Proposal"}
                  </button>
                ) : (
                  <button className="btn btn-download" onClick={handleDownload}>
                    ⬇ Download {downloadReady.filename}
                  </button>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}
